import React from 'react'
import { roundWithLocale } from 'utils/math'
import Header, { Title } from '../Header'
import { AttributeList, Blend } from '../Attribute'
import copy from './copy'
import Section from '../Section'
import Separator from '../Separator'
import FullCopyAttribute from '../FullCopyAttribute'

type AppearanceProps = {
  opacity: number
  blendMode: string
  radius?: number[]
  smoothCorners?: boolean
}

export const isAppearanceHidden = (
  opacity: number,
  blendMode: string,
  radius?: number[]
) =>
  !(radius && radius.length > 0 && radius.some(n => n !== 0)) &&
  opacity === 1 &&
  blendMode.toLowerCase() === 'normal'

const Appearance: React.FC<AppearanceProps> = ({
  opacity,
  blendMode,
  radius = [],
  smoothCorners,
}) => {
  if (isAppearanceHidden(opacity, blendMode, radius)) {
    return null
  }
  const hasRadius = radius && radius.length > 0 && radius.some(n => n !== 0)
  // Normalized radius is the radius array simplified to have only one element if all
  // elements are the same
  const normalizedRadius = (hasRadius && radius.every(r => r === radius[0])
    ? [radius[0]]
    : radius || []
  ).map(r => roundWithLocale(r, 2))

  const opacityPercentage = `${(opacity * 100).toFixed().toLocaleString()}%`

  const sectionCopyValue = copy({
    opacityPercentage,
    blendMode,
    radius: normalizedRadius.join(';'),
    smoothCorners,
  })

  return (
    <>
      <Separator />
      <Section data-testid="inspector-sidebar-appearance">
        <Header copyValue={sectionCopyValue}>
          <Title>Appearance</Title>
        </Header>
        <AttributeList>
          {(opacity !== 1 || blendMode.toLowerCase() !== 'normal') && (
            <FullCopyAttribute
              label="Opacity"
              value={
                <>
                  {opacityPercentage}
                  {blendMode.toLowerCase() !== 'normal' && (
                    <Blend blendMode={blendMode} />
                  )}
                </>
              }
              copyValue={`${radius}`}
            />
          )}
          {hasRadius && (
            <FullCopyAttribute
              label="Radius"
              value={`${normalizedRadius.join(';')} ${
                smoothCorners ? '(Smooth Corners)' : ''
              }`}
              copyValue={`${radius}`}
            />
          )}
        </AttributeList>
      </Section>
    </>
  )
}

export default Appearance
